import { Injectable } from '@angular/core';
import {
  GlobalIsolateSdkService,
  GlobalRequest
} from '@suzy/shared/data-access/global-isolate-sdk';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GlobalEndingComponent } from '../actions/global-ending/global-ending.component';
import { AssetPurpose, AssetType } from '@asksuzy/typescript-sdk';

@Injectable({
  providedIn: 'root'
})
export class GlobalService {
  private destroy$ = new Subject<boolean>();
  private monadicVariantId: string = '';
  private recentLanguage: string = '';

  constructor(
    private globalSDK: GlobalIsolateSdkService,
    private translate: TranslateService,
    private modals: NgbModal
  ) {}

  getAvailableAsync(missionId: string, cintId: string): Observable<any> {
    return this.globalSDK.Mission.getAvailableMissionsAsync(true).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  /* The same as this.globalSDK.Auth.validateCintRespondentAsync, with additional query parameter processing */
  getValidateEmergencyRespondentAsync(
    brand_id: string,
    suzy_mission_id: string,
    respondent_id: string,
    queryString: string
  ): Observable<any> {
    const request = new GlobalRequest();
    if (queryString && queryString.length > 0) {
      const params = new URLSearchParams(queryString);
      params.forEach((value, key) => {
        if (key == 'transaction_id') {
          // transaction_id is the query string version of {respondent_id}, if provided it supercedes
          respondent_id = value;
        } else {
          request.params[key] = value;
        }
      });
    }
    if (window.location.pathname.startsWith('/c/')) {
      // Use the complete current URL which already includes the rid parameter
      return this.globalSDK.Auth.validateCintDemandRespondentAsync({
        cint_demand_url: window.location.href,
        brand_id: brand_id,
        mission_id: suzy_mission_id
      });
    }

    request.method = 'POST';
    request.resource = 'auth/validate/cint/{suzy_mission_id}/{respondent_id}'; // same route for legacy support
    request.resource = request.resource.replace(
      /{suzy_mission_id}/g,
      suzy_mission_id.toString()
    );
    request.resource = request.resource.replace(
      /{respondent_id}/g,
      respondent_id.toString()
    );

    return this.globalSDK.execute(request);
  }

  getUploadInfoForActionAsync(
    actionId: string,
    assetType: AssetType,
    assetPurpose: AssetPurpose,
    name: string
  ): Observable<any> {
    const request = new GlobalRequest();
    request.method = 'POST';
    request.resource = `/actions/respond/${actionId}/prepare_upload`;
    request.params = {
      asset_type: assetType,
      asset_purpose: assetPurpose,
      name,
      verb: 'PUT',
      content_type: 'auto'
    };
    return this.globalSDK.execute(request);
  }

  performActionAsync() {
    const request = new GlobalRequest();
    request.method = 'POST';
    request.resource = `/missions/perform`;
    return this.globalSDK.execute(request);
  }

  getValidateCintRespondentAsync(
    brandId: string,
    missionId: string,
    cintId: string,
    queryString: string
  ): Observable<any> {
    return this.getValidateEmergencyRespondentAsync(
      brandId,
      missionId,
      cintId,
      queryString
    ).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  getMission(): Observable<any> {
    const forceRefresh = false;
    return this.globalSDK.Mission.getAvailableMissionsAsync(forceRefresh).pipe(
      catchError(err => {
        return throwError(err);
      })
    );
  }

  getCurrentMissionLanguage(): string {
    return this.recentLanguage;
  }

  setLanguage(lan: string): void {
    this.recentLanguage = lan;
    this.translate.addLangs([...this.translate.getLangs(), lan]);
    this.translate.use(lan);
    this.translate
      .getTranslation(lan)
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        this.translate.setTranslation(lan, { ...val.tokens });
      });
    this.globalSDK.setLanguage(lan);
  }

  respondToGlobalActionAsync({
    method = 'POST',
    resource = 'actions/respond',
    payload,
    params = ''
  }): Observable<any> {
    return this.globalSDK
      .execute({
        method,
        resource,
        payload,
        params
      })
      .pipe(
        map(data => {
          if (data.success) {
            if (data.item && Object.keys(data.item).length > 0) {
              const newAction = data.item;
              const applied_translation_locale =
                newAction['applied_translation_locale'];
              if (applied_translation_locale)
                this.setLanguage(applied_translation_locale);
            }
          }
          return data;
        })
      );
  }

  showGlobalEndingComponent(
    mission,
    action,
    actionTypeName,
    cintRedirect,
    message?,
    description?
  ): Subject<any> {
    const globalMissionContainer = document.querySelector(
      '#globalMission'
    ) as HTMLElement;
    if (globalMissionContainer) {
      globalMissionContainer.style.display = 'block';
    }
    const subject = new Subject<any>();
    const startTime = new Date().getTime();
    let endingModal = this.modals.open(GlobalEndingComponent, {
      windowClass: 'global-mission-container',
      container: '#globalMission',
      backdrop: false
    });

    const endingComponent =
      endingModal?.componentInstance as GlobalEndingComponent;
    if (action && endingComponent) {
      endingComponent.type =
        actionTypeName; /* Where I will get the action type  */
    }
    if (mission && endingComponent) {
      endingComponent.points = mission.points;
      endingComponent.mission = mission;
    }
    if (endingComponent) {
      endingComponent.startTime = startTime;
      endingComponent.cintRedirect = cintRedirect;
      endingComponent.message = message;
      endingComponent.description = description;

      endingModal.result.then(
        res => {
          if (res) {
            subject.next(res);
          } else {
            subject.next('refresh');
            return;
          }
        },
        () => {
          subject.next('refresh');
          this.modals.dismissAll();

          return;
        }
      );
    }
    return subject;
  }

  setMonadicVariantId(id: string): void {
    this.monadicVariantId = id;
  }

  getMonadicVariantId(): string {
    return this.monadicVariantId;
  }

  setApiKeyToGlobalSdk(keys: { api_key: string; api_secret: string }): void {
    this.globalSDK.setApiCredentials(keys.api_key, keys.api_secret);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
